import { Component, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { userDtos, userEnums } from '@vectronic/openApi-customer';
import { ConfirmPermissionComponent } from '@vectronic/ui';
import { DialogService, Item, ManagePermissionTableItemModel, roleRank } from '@vectronic/util';

@Component({
  selector: 'table-multi-role-selector',
  templateUrl: './multi-role-selector.component.html',
  styleUrls: ['./multi-role-selector.component.scss'],
  imports: [MatSelectModule, FormsModule],
})
export class MultiRoleSelectorComponent {
  @ViewChild(MatSelect) private roleSelect?: MatSelect;

  row = input<ManagePermissionTableItemModel>();
  item = input<Item>();
  allowOwner = input.required<boolean>();
  hideDeviceManager = input(false);
  currentUserEmail = input<string>();
  ownRole = input<userEnums.Role>('READER');
  canEditPermissions = input(true);
  canEditAllRoles = input(false);
  canModifyOwner = input(true);
  pendingRequestedShares = input<userDtos.SelfRequestedShare[]>([]);

  disabled = computed(
    () =>
      !this.isRowEnabled() ||
      this.hasUnavailablePendingRequest() ||
      !this.canModifyOwner() ||
      (!this.isOwnRow() && !this.canManageRow()),
  );

  updateShares = output<{
    item: Item;
    ids: number[];
    share: userDtos.Share;
    row: ManagePermissionTableItemModel;
  }>();

  requestHigherRole = output<{
    item: Item;
    ids: number[];
    role: Exclude<userEnums.Role, 'OWNER'>;
    row?: ManagePermissionTableItemModel;
  }>();
  withdrawRequestedShares = output<{
    item: Item;
    requests: userDtos.SelfRequestedShare[];
  }>();

  computedValue = computed<userEnums.Role | 'MULTI'>(() => {
    if (this.value()) return this.value();
    return this.row()?.role;
  });

  value = signal<userEnums.Role | 'MULTI'>(undefined);

  private dialogService = inject(DialogService);

  isOwnRow() {
    return this.row()?.userName === this.currentUserEmail();
  }

  isRowEnabled(): boolean {
    return this.row()?.isEnabled !== false;
  }

  hasPartialPendingRequest(): boolean {
    const selectedMemberCount = this.row()?.memberIds?.length ?? 0;
    const pendingRequestCount = this.pendingRequestedShares().length;
    return pendingRequestCount > 0 && pendingRequestCount !== selectedMemberCount;
  }

  hasMixedPendingRequestRoles(): boolean {
    const requestedRoles = this.pendingRequestedShares().map(({ requestedRole }) => requestedRole);
    return new Set(requestedRoles).size > 1;
  }

  hasUnavailablePendingRequest(): boolean {
    return this.hasPartialPendingRequest() || this.hasMixedPendingRequestRoles();
  }

  canManageRow(): boolean {
    if (!this.canEditPermissions()) return false;
    if (this.canEditAllRoles()) return true;
    const rowRole = this.row()?.role;
    if (rowRole && rowRole !== 'MULTI' && roleRank(rowRole) > roleRank(this.ownRole()))
      return false;
    return true;
  }

  canSelectRole(role: userEnums.Role | 'MULTI'): boolean {
    if (role === 'MULTI') return false;
    if (!this.isRowEnabled()) return false;
    if (this.hasUnavailablePendingRequest()) return false;
    if (!this.canEditPermissions()) return false;
    if (!this.canModifyOwner()) return false;
    if (this.canEditAllRoles()) return true;
    if (this.canUpgradeToRole(role)) return false;
    if (this.isOwnRow()) return this.canEditPermissions();
    if (!this.canManageRow()) return false;
    if (this.ownRole() === 'OWNER') return true;
    return roleRank(role) <= roleRank(this.ownRole());
  }

  canUpgradeToRole(role: string): boolean {
    if (!this.isRowEnabled()) return false;
    if (this.hasUnavailablePendingRequest()) return false;
    if (this.canEditAllRoles()) return false;
    if (role === 'OWNER') return false;
    if (!this.isOwnRow()) return false;
    const currentRole = this.row()?.role;
    if (!currentRole) return false;
    if (currentRole === 'MULTI') {
      const collarRoles = this.row()?.collarRoles ?? {};
      return Object.values(collarRoles).some((collarRole) => roleRank(role) > roleRank(collarRole));
    }
    return roleRank(role) > roleRank(currentRole);
  }

  canShowUpgradeAction(role: Exclude<userEnums.Role, 'OWNER'>): boolean {
    return (
      this.canUpgradeToRole(role) &&
      (!this.hasPendingRequestForAnyRole() || this.hasPendingRequest(role))
    );
  }

  isMixedUpgradeAction(role: Exclude<userEnums.Role, 'OWNER'>): boolean {
    if (!this.canShowUpgradeAction(role)) return false;
    const targetRank = roleRank(role);
    const ranks = Object.values(this.row()?.collarRoles ?? {}).map((collarRole) =>
      roleRank(collarRole),
    );
    return ranks.some((rank) => rank < targetRank) && ranks.some((rank) => rank > targetRank);
  }

  hasPendingRequest(role: Exclude<userEnums.Role, 'OWNER'>): boolean {
    return Boolean(this.pendingRequestsForRole(role).length);
  }

  hasPendingRequestForAnyRole(): boolean {
    return Boolean(this.pendingRequestedShares().length);
  }

  pendingRequestsForRole(role: Exclude<userEnums.Role, 'OWNER'>) {
    return this.pendingRequestedShares().filter(({ requestedRole }) => requestedRole === role);
  }

  onSubmit() {
    const share = { email: this.row().userName, role: this.value() as userEnums.Role };
    const ids = this.row().memberIds;
    this.updateShares.emit({ ids, share, item: this.item(), row: this.row() });
  }

  onSelectedRole(value: userEnums.Role | 'MULTI') {
    const isUpgradePath = this.canUpgradeToRole(value);
    if (!this.canSelectRole(value) && !isUpgradePath) return;
    if (isUpgradePath) return;
    this.value.set(value);
    if (value === 'DEVICE_MANAGER' || value === 'DATA_MANAGER' || value === 'OWNER') {
      this.dialogService.openWide(ConfirmPermissionComponent, {
        data: { type: this.item(), multiple: true, role: value },
        afterConfirm: ({ confirm }) => {
          if (confirm) this.onSubmit();
        },
      });
    } else if (value === 'READER') this.onSubmit();
  }

  onRequestHigherRole(role: Exclude<userEnums.Role, 'OWNER'>) {
    if (!this.canShowUpgradeAction(role)) return;
    this.roleSelect?.close();
    this.requestHigherRole.emit({
      item: this.item(),
      ids: this.row().memberIds,
      role,
      row: this.row(),
    });
  }

  onWithdrawRequestedShare(role: Exclude<userEnums.Role, 'OWNER'>) {
    if (!this.canShowUpgradeAction(role)) return;
    const requests = this.pendingRequestsForRole(role);
    if (!requests.length) return;
    this.roleSelect?.close();
    this.withdrawRequestedShares.emit({ item: this.item(), requests });
  }
}
